import {
  ConsoleSection,
  FinaleSection,
  SetupSection,
  StatementSection,
} from "@/components/landing/LandingSections";
import { StayStory } from "@/components/landing/StayStory";
import { StayStrip } from "@/components/landing/StayStrip";
import { prefersReducedMotion, startLandingMotion } from "@/components/landing/landing-motion";
import { HERO } from "@/content/landing";
import { COMPANY } from "@/lib/company";
import { takePrerendered } from "@/lib/prerender";
import { DEFAULT_SITE_ORIGIN, normalizeOrigin } from "@/lib/seo";
import { useHydrated } from "@/lib/use-hydrated";
import { useAuthStore } from "@/stores/auth";
import "@/styles/landing.css";
import { ArrowRight } from "lucide-react";
import { useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";

const SITE_ORIGIN = normalizeOrigin(import.meta.env.VITE_SITE_ORIGIN ?? DEFAULT_SITE_ORIGIN);

// 구조화 데이터는 마크업에 직접 싣는다 — 프리렌더된 HTML 에 들어가야 크롤러가 JS 없이 읽는다.
const JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "스테이라인",
  alternateName: COMPANY.serviceName,
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  url: `${SITE_ORIGIN}/`,
  description: HERO.lede,
  publisher: { "@type": "Organization", name: COMPANY.name, email: COMPANY.email },
});

/**
 * 공개 랜딩. 콘셉트는 "한 줄의 체류" — 페이지가 게스트 한 명의 체류(예약 → 체크아웃)를
 * 그린 선 하나이고, 스크롤이 곧 체류 시간이다. 기획: src/components/landing/DESIGN.md.
 */
export function LandingPage() {
  // Signed-in operators get a direct console link instead of the login CTA.
  // 프리렌더는 늘 로그아웃 상태로 그려지므로 hydrate 가 끝난 뒤에 로그인 여부를 반영한다.
  const hydrated = useHydrated();
  const isAuthed = useAuthStore((s) => Boolean(s.token)) && hydrated;
  const consoleTo = isAuthed ? "/admin" : "/admin/login";
  const consoleCta = isAuthed ? "콘솔로 이동" : "콘솔 시작하기";

  const rootRef = useRef<HTMLDivElement>(null);

  // 첫 페인트 전에 시작해야 글이 한 번 보였다가 숨는 깜빡임이 없다.
  // `sl-motion`(드러내기 전 숨김)은 여기서 붙인다 — 렌더에서 matchMedia 를 읽으면 프리렌더
  // 마크업과 어긋나고, 동작 줄이기 사용자는 숨은 글이 영영 안 드러난다.
  // 프리렌더된 첫 화면을 hydrate 한 경우엔 이미 보이는 첫 화면 글을 다시 숨기지 않는다(인트로 생략).
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const intro = !takePrerendered("/");
    if (!prefersReducedMotion()) root.classList.add("sl-motion");
    try {
      return startLandingMotion(root, { intro });
    } catch {
      root.classList.remove("sl-motion");
    }
  }, []);

  return (
    <div ref={rootRef} className="sl">
      <script
        type="application/ld+json"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: 고정된 JSON-LD 문자열(사용자 입력 없음)
        dangerouslySetInnerHTML={{ __html: JSON_LD }}
      />
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section
        aria-labelledby="hero-title"
        className="mx-auto flex min-h-[calc(100svh-4rem)] w-full max-w-6xl flex-col gap-12 px-4 pb-6 pt-14 sm:px-6 md:pt-16"
      >
        <div className="grid flex-1 items-center gap-y-10 md:grid-cols-12 md:gap-x-8">
          <div className="md:col-span-8 lg:col-span-9">
            {/* h1 은 숨기지 않는다 — 가장 큰 글이라 LCP 가 이 글자에 걸린다. */}
            <h1 id="hero-title" className="sl-mega">
              {HERO.titleLines[0]}
              <br />
              {HERO.titleLines[1]}
            </h1>
            <p
              className="sl-body sl-muted mt-8 max-w-[34rem]"
              data-split
              data-intro
              data-delay="0.1"
            >
              {HERO.lede}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to={consoleTo} className="sl-btn">
                {consoleCta}
                <ArrowRight aria-hidden className="sl-btn__arrow h-4 w-4" />
              </Link>
              <Link to={HERO.secondaryCta.to} className="sl-btn sl-btn--ghost">
                {HERO.secondaryCta.label}
              </Link>
            </div>
            <p className="sl-muted mt-5 flex items-center gap-2 text-[13px]">
              <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[var(--sl-accent)]" />
              {HERO.betaNote}
            </p>
          </div>
          <figure
            className="overflow-hidden rounded-[28px] md:col-span-4 lg:col-span-3"
            data-reveal
            data-intro
            data-delay="0.15"
          >
            <img
              src={HERO.image.src}
              srcSet={HERO.image.srcSet}
              sizes={HERO.image.sizes}
              alt={HERO.image.alt}
              width={HERO.image.width}
              height={HERO.image.height}
              fetchPriority="high"
              decoding="async"
              className="aspect-[4/3] w-full object-cover object-[62%_50%] md:aspect-[3/4] md:max-h-[64svh]"
            />
          </figure>
        </div>
        <StayStrip />
      </section>

      <StayStory />
      <StatementSection />
      <ConsoleSection />
      <SetupSection />
      <FinaleSection consoleTo={consoleTo} consoleCta={consoleCta} />
    </div>
  );
}
